import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bot, RefreshCw, CheckCircle, XCircle, Clock, Zap, AlertTriangle } from 'lucide-react';
import { useAdminT } from '@/lib/adminTranslations';

const API_BASE = '';

export default function AdminBotMonitor() {
  const t = useAdminT();
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/bot-monitor`, { headers });
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 15000); return () => clearInterval(i); }, [fetchData]);

  if (loading) return <div className="p-6 text-slate-400">{t.loading}</div>;

  const statusColors = {
    active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    idle: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    never_run: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  };

  return (
    <div className="space-y-6" data-testid="admin-bot-monitor">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100" style={{ fontFamily: 'var(--font-heading)' }}>
          <Bot className="w-6 h-6 inline mr-2 text-violet-400" /> Bot Monitor
        </h1>
        <Button size="sm" onClick={fetchData} className="bg-slate-800 text-slate-300 hover:bg-slate-700">
          <RefreshCw className="w-4 h-4 mr-1" /> {t.refresh}
        </Button>
      </div>

      <p className="text-xs text-slate-500">Auto-refresh every 15s</p>

      {/* Bot Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data?.bots?.map((bot, i) => (
          <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid={`bot-card-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-200">{bot.name}</h3>
              <Badge className={statusColors[bot.status] || statusColors.idle}>
                {bot.status === 'active' ? <CheckCircle className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                {bot.status}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mb-3">{bot.description}</p>

            {bot.interval && (
              <div className="text-xs text-slate-600 mb-2">
                <Clock className="w-3 h-3 inline mr-1" /> Interval: {bot.interval} | Mode: {bot.mode}
              </div>
            )}

            {bot.last_run && bot.last_run !== 'never' && (
              <div className="text-xs text-slate-600 mb-2">
                Last run: {new Date(bot.last_run).toLocaleString()}
              </div>
            )}

            {/* Stats */}
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(bot.stats || {}).map(([key, val]) => {
                const isAlert = key === 'today_failed' && val > 0;
                const isPending = key.includes('pending') && val > 0;
                return (
                  <div key={key} className={`text-xs px-2 py-1 rounded border ${
                    isAlert ? 'border-red-500/20 bg-red-500/10 text-red-400' :
                    isPending ? 'border-amber-500/20 bg-amber-500/10 text-amber-400' :
                    'border-slate-700/50 bg-slate-800/50 text-slate-300'
                  }`}>
                    <span className="text-slate-500">{key.replace(/_/g, ' ')}: </span>
                    <span className="font-bold tabular-nums">{val}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Recent AI Jobs */}
      {data?.recent_ai_jobs?.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Recent AI Jobs</h3>
          <div className="space-y-1">
            {data.recent_ai_jobs.map((job, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-slate-800/50">
                <div className="flex items-center gap-2">
                  {job.success ? <CheckCircle className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                  <span className="text-slate-400">{job.page_type}</span>
                  <span className="text-slate-300 truncate max-w-[200px]">{job.page_key}</span>
                  <Badge variant="outline" className="border-slate-700 text-slate-500">{job.language}</Badge>
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  {job.tokens_used > 0 && <span>{job.tokens_used}t</span>}
                  {job.generation_time_ms > 0 && <span>{(job.generation_time_ms / 1000).toFixed(1)}s</span>}
                  <span>{new Date(job.timestamp).toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
